// WhatsApp AI Docs — service files for the WhatsApp agent, kept in an OpenAI vector store as in the
// reference product. Files go straight to OpenAI (no database copy); the agent searches them with
// file_search. A file with the same name as one already in the store replaces it.
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaCheckCircle, FaClock, FaFileAlt } from 'react-icons/fa';
import api, { formatDateTime } from '../services/api';
import { Confirm, Loader, Spinner, StatCard, StatGrid } from '../components/ui';

const ACCEPT = ['pdf', 'txt', 'md', 'docx', 'doc', 'json', 'html', 'pptx', 'csv'];
const MAX_BYTES = 20 * 1024 * 1024;
const POLL_MS = 5000;

const STATUS = {
  completed: { label: 'Ready', tone: 'ok' },
  in_progress: { label: 'Indexing', tone: 'info' },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
};

function sizeLabel(bytes) {
  if (!bytes) return '';
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function uploadedAt(value) {
  if (!value) return '';
  return formatDateTime(typeof value === 'number' ? value * 1000 : value);
}

function SetupCard({ envKey, missing, error, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const create = async () => {
    setCreating(true);
    try {
      const res = await api.createVectorStore();
      setCreated(res);
      toast.success(`Vector store ${res.vector_store_id} created`);
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="nye-panel" style={{ marginBottom: 24 }}>
      <div className="nye-strong" style={{ fontSize: 17, marginBottom: 8 }}>
        Set up the knowledge base
      </div>
      {missing ? <div className="nye-callout nye-callout--danger" style={{ marginBottom: 14 }}>{error}</div> : null}
      <div className="nye-hint" style={{ marginBottom: 14 }}>
        Service files are kept in the AI knowledge base. {missing ? 'The saved knowledge base cannot be used.' : 'No knowledge base is set yet.'} Create one here,
        or put the id of an existing one in <code>backend/.env</code> as <code>{envKey}</code>, then restart the backend.
      </div>
      {created ? (
        <div className="nye-callout">
          <strong>Created.</strong> It works now. To keep it after a restart, add this line to <code>nyufaz-analyze/backend/.env</code>:
          <pre className="nye-pre nye-mt-8" style={{ maxHeight: 'none' }}>{created.env_line}</pre>
          <button
            type="button"
            className="nye-btn nye-btn--outline nye-btn--xs nye-mt-8"
            onClick={() => navigator.clipboard?.writeText(created.env_line).then(() => toast.success('Copied'))}
          >
            Copy line
          </button>
        </div>
      ) : (
        <button type="button" className="nye-btn nye-btn--primary" onClick={create} disabled={creating}>
          {creating ? <Spinner light /> : null}
          {missing ? 'Create a new vector store' : 'Create vector store'}
        </button>
      )}
    </div>
  );
}

export default function RagUpload() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [removing, setRemoving] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const fileRef = useRef(null);

  const load = (silent) =>
    api
      .listRag()
      .then((d) => {
        setData(d);
        setLoadError('');
      })
      .catch((e) => {
        setLoadError(e.message);
        if (!silent) toast.error(e.message || 'Failed to load documents');
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    load(false);
  }, []);

  const docs = data?.files || [];
  const indexing = docs.some((d) => d.status === 'in_progress');

  useEffect(() => {
    if (!indexing) return undefined;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [indexing]);

  const pick = (list) => {
    const valid = Array.from(list || []).filter((f) => {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!ACCEPT.includes(ext)) {
        toast.error(`"${f.name}" — upload PDF, TXT, MD, DOCX, DOC, JSON, HTML, PPTX or CSV`);
        return false;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" is larger than 20 MB`);
        return false;
      }
      return true;
    });
    if (valid.length) setFiles((p) => [...p, ...valid]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const res = await api.uploadRag(f);
        ok += 1;
        if (res.status === 'failed') toast.error(`${f.name}: the knowledge base could not index it. ${res.error || ''}`);
        else toast.success(`${f.name} ${res.replaced ? 'replaced the old copy' : 'added'} (${STATUS[res.status]?.label || res.status})`);
      } catch (e) {
        toast.error(`${f.name}: ${e.message}`);
      }
    }
    setFiles([]);
    setUploading(false);
    if (ok) await load(true);
  };

  const remove = async () => {
    const doc = confirmRemove;
    setConfirmRemove(null);
    setRemoving(doc.id);
    try {
      await api.deleteRag(doc.id);
      toast.success(`${doc.filename} removed from the knowledge base`);
      await load(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRemoving(null);
    }
  };

  if (loading) return <Loader />;

  const store = data?.vector_store;
  const counts = store?.file_counts || {};
  const shown = docs.filter((d) => filter === 'all' || d.status === filter);

  return (
    <div className="nye-fade">
      <div className="nye-row nye-row--between nye-row--wrap" style={{ marginBottom: 20, alignItems: 'flex-start' }}>
        <div>
          <div className="nye-page-title">WhatsApp AI Docs</div>
          <div className="nye-page-sub">
            Upload the service files the WhatsApp AI agent answers from: service names, prices, durations and details. Files are stored in the
            AI knowledge base and searched on every message. The voice agent answers service questions from its own knowledge base, added on the voice agent, not from these files.
          </div>
        </div>
        {data?.configured ? (
          <button type="button" className="nye-btn nye-btn--outline" onClick={() => load(false)}>
            ↻ Refresh
          </button>
        ) : null}
      </div>

      {loadError ? <div className="nye-callout nye-callout--danger nye-mb-16">{loadError}</div> : null}
      {data && !data.configured ? (
        <SetupCard envKey={data.env_key} missing={data.missing} error={data.error} onCreated={() => load(true)} />
      ) : null}

      <StatGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        <StatCard variant="deep" label="Total Documents" value={counts.total ?? docs.length} icon={<FaFileAlt />} />
        <StatCard variant="deep" label="Ready for the Agent" value={counts.completed ?? 0} icon={<FaCheckCircle />} />
        <StatCard
          variant="deep"
          label={counts.failed ? 'Failed' : 'Indexing'}
          value={counts.failed ? counts.failed : counts.in_progress ?? 0}
          icon={<FaClock />}
        />
      </StatGrid>

      <div className="nye-g2 nye-g2--auto" style={{ gap: 24 }}>
        <div className="nye-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="nye-strong" style={{ fontSize: 17 }}>
            Upload documents
          </div>
          <div className="nye-field">
            <span className="nye-label--caps">Knowledge base</span>
            <div className="nye-input" style={{ cursor: 'default' }}>
              {store ? (
                <>
                  {store.name || 'Vector store'} <span className="nye-mono nye-muted">· {store.id}</span>
                </>
              ) : (
                'Not set up yet'
              )}
            </div>
          </div>
          <div
            className={`nye-drop${dragOver ? ' nye-drop--over' : ''}`}
            onClick={() => data?.configured && fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (data?.configured) pick(e.dataTransfer.files);
            }}
            style={data?.configured ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
          >
            <div className="nye-strong" style={{ marginBottom: 4 }}>
              Click or drop files
            </div>
            <div className="nye-small nye-muted">PDF, TXT, MD, DOCX, DOC, JSON, HTML, PPTX, CSV · max 20 MB</div>
            <input ref={fileRef} type="file" multiple accept={ACCEPT.map((e) => `.${e}`).join(',')} style={{ display: 'none' }} onChange={(e) => pick(e.target.files)} />
          </div>

          {files.length ? (
            <div>
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="nye-row" style={{ padding: '10px 12px', background: 'var(--nye-input-bg)', borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="nye-strong nye-ellipsis">{f.name}</div>
                    <div className="nye-small nye-muted">{sizeLabel(f.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="nye-btn nye-btn--ghost nye-btn--xs"
                    style={{ color: 'var(--nye-danger)' }}
                    onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}
                    disabled={uploading}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="nye-btn nye-btn--primary nye-btn--block" onClick={upload} disabled={uploading} style={{ padding: 12 }}>
                {uploading ? <Spinner light /> : null}
                {uploading ? 'Uploading and indexing…' : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
              </button>
            </div>
          ) : null}

          <div className="nye-callout">
            A file with the same name as one already uploaded replaces it, so upload the new price list with the same file name. The agent uses
            every Ready file, so remove files you no longer want it to read.
          </div>
        </div>

        <div className="nye-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 420 }}>
          <div className="nye-row nye-row--between">
            <div className="nye-strong" style={{ fontSize: 17 }}>
              Document history
            </div>
            {indexing ? (
              <span className="nye-small nye-muted nye-row" style={{ gap: 6 }}>
                <Spinner /> Indexing…
              </span>
            ) : null}
          </div>
          <div className="nye-row nye-row--wrap" style={{ gap: 6 }}>
            {[
              ['all', 'All'],
              ['completed', 'Ready'],
              ['in_progress', 'Indexing'],
              ['failed', 'Failed'],
            ].map(([id, label]) => (
              <button key={id} type="button" className={`nye-chip nye-chip--pill${filter === id ? ' active' : ''}`} onClick={() => setFilter(id)}>
                {label}
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <div className="nye-empty">
              {docs.length ? 'No documents in this view' : 'No files yet. Until one is uploaded the agent will not quote prices or durations.'}
            </div>
          ) : (
            shown.map((d) => {
              const st = STATUS[d.status] || { label: d.status, tone: 'muted' };
              return (
                <div key={d.id} className={`nye-file-card${d.status === 'completed' ? ' nye-file-card--active' : ''}`}>
                  <div className="nye-row nye-row--between" style={{ alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="nye-row nye-row--wrap" style={{ gap: 8, marginBottom: 4 }}>
                        <span className="nye-strong nye-ellipsis">{d.filename}</span>
                        <span className="nye-pill nye-pill--square nye-pill--sm">Salon</span>
                        <span className={`nye-pill nye-pill--square nye-pill--sm nye-pill--${st.tone}`}>{st.label}</span>
                      </div>
                      <div className="nye-small nye-muted">
                        Added {uploadedAt(d.created_at)}
                        {d.bytes ? ` · ${sizeLabel(d.bytes)}` : ''}
                      </div>
                      <div className="nye-small nye-mono nye-muted">{d.id}</div>
                      {d.error ? (
                        <div className="nye-small" style={{ color: 'var(--nye-danger)', marginTop: 4 }}>
                          {d.error}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="nye-icon-action nye-icon-action--delete"
                      title="Remove from the knowledge base"
                      onClick={() => setConfirmRemove(d)}
                      disabled={removing === d.id}
                    >
                      {removing === d.id ? <Spinner /> : '✕'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {confirmRemove ? (
        <Confirm
          title="Remove document?"
          message={`Remove "${confirmRemove.filename}" from the knowledge base? The WhatsApp agent stops using it straight away.`}
          confirmLabel="Remove"
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      ) : null}
    </div>
  );
}
